import type { FormTypeBreakdownItem } from '#services/api/insights.ts';

import * as React from 'react';
import {
  faArrowDownWideShort,
  faArrowUpWideShort,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeScalableIcon, IconButton } from '@mend/mui';
import {
  Box,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';

import { SeeMore } from '#components/SeeMore/SeeMore.tsx';
import { useSeeMore } from '#components/SeeMore/use-see-more.ts';
import { formTypeBreakdown } from '#lib/react-query/queries.ts';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import { formatDurationFromSeconds } from '#utils/dates.ts';
import { Base } from './Base.tsx';
import { useFormWidget } from './use-form-widget.ts';

const widgetTitle = 'Form Usage and Completion Time';

type SortMode = 'assigned' | 'completion' | 'speed';
type SortDirection = 'asc' | 'desc';
type Sort = { mode: SortMode; direction: SortDirection };

const toggleButtonSx = { whiteSpace: 'nowrap' } as const;

function GridRow(props: { item: FormTypeBreakdownItem }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  function maybeRenderRootWrapper(jsx: JSX.Element) {
    if (!isMobile) return jsx;
    return (
      <Stack direction="column" overflow="hidden">
        {jsx}
      </Stack>
    );
  }

  function maybeRenderGroupWrapper(jsx: JSX.Element) {
    if (!isMobile) return jsx;
    return <Stack alignItems="center">{jsx}</Stack>;
  }

  return maybeRenderRootWrapper(
    <>
      {maybeRenderGroupWrapper(
        <>
          <Chip
            size="small"
            label={props.item.assessmentTypeRequired ? 'Required' : 'Optional'}
            color={props.item.assessmentTypeRequired ? 'error' : 'neutral'}
          />

          {/* TODO: Confirm approach in mobile because it's not ideal */}
          <Tooltip title={props.item.assessmentTypeName}>
            <Typography
              variant="body2"
              fontWeight={500}
              maxWidth="100%"
              justifySelf="start"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {props.item.assessmentTypeName}
            </Typography>
          </Tooltip>
        </>
      )}

      {maybeRenderGroupWrapper(
        <>
          <Typography variant="body2" color="text.secondary">
            {`${props.item.completed}/${props.item.assigned} completed`}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {props.item.medianCompletionSeconds > 0
              ? formatDurationFromSeconds(
                  props.item.medianCompletionSeconds,
                  'short'
                )
              : 'n/a'}
          </Typography>
        </>
      )}

      {maybeRenderGroupWrapper(
        <>
          <LinearProgress
            color="primary"
            variant="determinate"
            value={props.item.completionRate}
            sx={{
              // These styles are intended one-offs
              height: 32,
              borderRadius: 2,
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
              },
              flexGrow: isMobile ? 1 : undefined,
            }}
          />

          <Typography
            variant="body1"
            width="55px" // To maintain consistent width across mobile/desktop
            textAlign="right"
            fontWeight={600}
          >
            {`${props.item.completionRate}%`}
          </Typography>
        </>
      )}
    </>
  );
}

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows, for each form type, the&nbsp;
        <InfoEmphasis>total number of forms assigned</InfoEmphasis>, the&nbsp;
        <InfoEmphasis>percentage completed</InfoEmphasis>, and the&nbsp;
        <InfoEmphasis>median time to complete</InfoEmphasis>. This helps
        organizations identify bottlenecks in form completion, evaluate the
        effectiveness of forms as an engagement tool, and monitor patient
        preparedness workflows.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Assigned" indented>
        <InfoList
          items={[
            'Count of forms that were assigned within the selected date range.',
          ]}
        />
      </InfoSection>
      <InfoSection title="Completed" indented>
        <InfoList
          items={[
            'Count of completed forms from that same assigned group (even if the form was completed after the selected date range).',
          ]}
        />
      </InfoSection>
      <InfoSection title="Completion %" indented>
        <InfoList
          items={[
            'Percentage of assigned forms that were completed: (Completed ÷ Assigned) × 100.',
          ]}
        />
      </InfoSection>
      <InfoSection title="Time to Complete" indented>
        <InfoList
          items={[
            <>
              For completed forms in the dataset:
              <InfoList
                component="ol"
                boxProps={{ sx: { listStyleType: 'lower-alpha', pl: 3 } }}
                items={[
                  'Measure the time from when the form was assigned to when it was completed',
                  <>
                    Group by form type and calculate the{' '}
                    <InfoEmphasis>median</InfoEmphasis> completion time (not the
                    average)
                  </>,
                  <>
                    Display as:
                    <InfoList
                      items={[
                        'Minutes if under 1 hour',
                        'Hours if under 1 day',
                        'Days if 1 day or more (rounded to 1 decimal place)',
                      ]}
                    />
                  </>,
                ]}
              />
            </>,
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms assigned within the selected date range',
            'Completed forms for those assignments (even if completion occurs later)',
            'Forms not marked as deleted or canceled',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Deleted forms',
            'Canceled forms',
            'Forms assigned outside the selected date range (even if completed inside it)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Sorting by metric can help highlight top- or bottom-performing form types',
            <>
              The completion percentage and completion time are based{' '}
              <InfoEmphasis>only</InfoEmphasis>
              &nbsp;on forms assigned in the selected range
            </>,
            'Median is used for completion time to reduce skew from extreme delays',
            'The median completion time for each form type is calculated separately',
            'Data updates nightly, so very recent changes may not yet appear',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

function processBreakdownData(
  apiData: FormTypeBreakdownItem[]
): FormTypeBreakdownItem[] {
  const assessmentNameCount: Record<string, number> = {};
  const uniqueNameMap: Record<number, string> = {};

  // Process data and count occurrences of each name in a single pass
  apiData.forEach((item) => {
    const name = item.assessmentTypeName;
    assessmentNameCount[name] = (assessmentNameCount[name] || 0) + 1;
  });

  // Create unique display names where needed
  apiData.forEach((item) => {
    const name = item.assessmentTypeName;
    if ((assessmentNameCount[name] || 0) > 1) {
      uniqueNameMap[item.assessmentTypeId] =
        `${name} (${item.assessmentTypeId})`;
    } else {
      uniqueNameMap[item.assessmentTypeId] = name;
    }
  });

  let topAssigned: FormTypeBreakdownItem | null = null;
  let topCompletion: FormTypeBreakdownItem | null = null;
  let fastestCompletion: FormTypeBreakdownItem | null = null;

  apiData.forEach((item) => {
    if (
      item.assigned > 0 &&
      (!topAssigned || item.assigned > topAssigned.assigned)
    ) {
      topAssigned = item;
    }

    if (item.completed > 0) {
      if (
        !topCompletion ||
        item.completionRate > topCompletion.completionRate
      ) {
        topCompletion = item;
      }

      if (
        item.medianCompletionSeconds > 0 &&
        (!fastestCompletion ||
          item.medianCompletionSeconds <
            fastestCompletion.medianCompletionSeconds)
      ) {
        fastestCompletion = item;
      }
    }
  });

  return apiData.map((item) => ({
    ...item,
    assessmentTypeName:
      uniqueNameMap[item.assessmentTypeId] ?? item.assessmentTypeName,
  }));
}

export function FormTypeBreakdown() {
  const widget = useFormWidget();

  const [sort, setSort] = React.useState<Sort>({
    mode: 'assigned',
    direction: 'desc',
  });

  const { data, isLoading } = useQuery({
    ...formTypeBreakdown(widget.getCommonRequestParams()),
    select: processBreakdownData,
  });

  const sortedItems = React.useMemo(() => {
    return [...(data ?? [])].sort((a, b) => {
      if (a.assigned === 0 && b.assigned > 0) return 1;
      if (a.assigned > 0 && b.assigned === 0) return -1;

      const [x, y] = sort.direction === 'asc' ? [a, b] : [b, a];

      switch (sort.mode) {
        case 'assigned': {
          const diff = x.assigned - y.assigned;
          return diff !== 0 ? diff : x.completionRate - y.completionRate;
        }
        case 'completion': {
          const diff = x.completionRate - y.completionRate;
          return diff !== 0 ? diff : x.assigned - y.assigned;
        }
        case 'speed': {
          if (x.medianCompletionSeconds === 0 && y.medianCompletionSeconds > 0)
            return -1;
          if (x.medianCompletionSeconds > 0 && y.medianCompletionSeconds === 0)
            return 1;
          const diff = x.medianCompletionSeconds - y.medianCompletionSeconds;
          return diff !== 0 ? diff : x.assigned - y.assigned;
        }
        default:
          return 0;
      }
    });
  }, [data, sort]);

  const seeMore = useSeeMore({ data: sortedItems });

  const theme = useTheme();
  const toggleButtonOrientation = useMediaQuery(theme.breakpoints.down('md'))
    ? 'vertical'
    : 'horizontal';

  if (isLoading) {
    return (
      <Base title={widgetTitle} size="full">
        <Box sx={{ p: 2, pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Top forms sorted by usage, completion %, or speed
          </Typography>
          <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={32} />
            ))}
          </Stack>
        </Box>
      </Base>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Base
        title={widgetTitle}
        size="full"
        description="Top forms sorted by usage, completion %, or speed"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={300}
        >
          <Typography variant="body2" color="text.secondary">
            No form type data available
          </Typography>
        </Box>
      </Base>
    );
  }

  return (
    <Base
      title={widgetTitle}
      infoTemplate={infoTemplate}
      hideExpandButton
      size="full"
      description="Top forms sorted by usage, completion %, or speed"
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: 'min-content 1fr',
          md: 'min-content 1fr min-content',
        }}
        alignItems="center"
        marginBottom={2}
        gap={2}
      >
        <ToggleButtonGroup
          exclusive
          color="primary"
          value={sort.mode}
          orientation={toggleButtonOrientation}
          onChange={(_, newSortMode: SortMode | null) => {
            if (newSortMode !== null) {
              setSort({ mode: newSortMode, direction: 'desc' });
            }
          }}
          size="medium"
          aria-label="Sort by"
          sx={{ gridColumn: { xs: '1 / -1', md: '1 / 2' } }}
        >
          <ToggleButton value="assigned" sx={toggleButtonSx}>
            Assigned
          </ToggleButton>
          <ToggleButton value="completion" sx={toggleButtonSx}>
            Completion %
          </ToggleButton>
          <ToggleButton value="speed" sx={toggleButtonSx}>
            Time To Complete
          </ToggleButton>
        </ToggleButtonGroup>

        <IconButton
          size="medium"
          color="neutral"
          variant="contained"
          onClick={() =>
            setSort({
              ...sort,
              direction: sort.direction === 'desc' ? 'asc' : 'desc',
            })
          }
        >
          <FontAwesomeScalableIcon
            icon={
              sort.direction === 'desc'
                ? faArrowDownWideShort
                : faArrowUpWideShort
            }
            fontSize="inherit"
          />
        </IconButton>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          md: 'auto 2fr auto auto 3fr auto',
        }}
        gap={{ xs: 3, md: 2 }}
        alignItems="center"
      >
        {seeMore.data.map((item) => (
          <GridRow key={item.assessmentTypeId} item={item} />
        ))}
      </Box>

      {seeMore.canSeeMore && (
        <Box mt={2}>
          <SeeMore
            flag={seeMore.flag}
            additional={seeMore.additional}
            toggle={seeMore.toggle}
          />
        </Box>
      )}
    </Base>
  );
}
